export enum EChartType {
  BAR = "bar",
  LINE = "line",
  AREA = "area",
  PIE = "pie",
}

// NOTE: How a chart value reads in its tooltip. `currency:XXX` takes an ISO
// 4217 code; `percent` expects the number already in percent (12 reads "12%").
export type TChartValueFormat =
  | "number"
  | "integer"
  | "percent"
  | `currency:${string}`;

export interface ICartesianChartSeries {
  name: string;
  data: number[];
}

// NOTE: `subtitle` names the unit, legend or time range and `source` names
// where the numbers come from — the two lines of the editorial card that the
// model fills in around the figure. Both are optional so every chart the model
// has ever emitted still validates.
// NOTE: `colors` is set only when the user asked for specific colours: one
// hex means "shades of this hue", several mean "one per series or slice in
// this order". A null entry keeps that series or slice in its palette colour.
// Absent, the figure paints in the organisation's palette.
// NOTE: NSAI-7855 — `format` says what the numbers are (money, percent), so a
// hovered value reads "$26,507" like the stat tiles beside it. Absent, a value
// is a plain number rounded to two decimals.
export interface ICartesianChartData {
  type: EChartType.BAR | EChartType.LINE | EChartType.AREA;
  categories: string[];
  series: ICartesianChartSeries[];
  title?: string;
  subtitle?: string;
  source?: string;
  colors?: Array<string | null>;
  format?: TChartValueFormat;
}

export interface IPieChartSlice {
  name: string;
  value: number;
}

export interface IPieChartData {
  type: EChartType.PIE;
  slices: IPieChartSlice[];
  title?: string;
  subtitle?: string;
  source?: string;
  colors?: Array<string | null>;
}

export interface ICartesianChartSeriesMeta {
  key: string;
  name: string;
}

export interface ICartesianChartRow {
  category: string;
  [key: string]: string | number;
}

export type TChartData = ICartesianChartData | IPieChartData;

// NOTE: Interfaces moved from utils/chart-transform.ts (Issue 2)
export interface ICartesianChartTransformResult {
  rows: ICartesianChartRow[];
  series: ICartesianChartSeriesMeta[];
}

export interface IPieDataItem {
  name: string;
  value: number;
}

export interface IBuildSeriesKeyProps {
  index: number;
}

export interface ITransformToCartesianChartDataProps {
  chartData: ICartesianChartData;
}

export interface ITransformToPieDataProps {
  slices: IPieChartSlice[];
}

export interface IFilterLabelsForDisplayProps {
  labels: string[];
  maxLabels: number;
}

// NOTE: Interfaces moved from utils/chart-colors.ts (Issue 3)
export interface IApplyOpacityProps {
  color: string;
  opacity: number;
}

export interface IGetChartSeriesColorsProps {
  seriesCount: number;
  resolveColor: (key: string) => string;
}

// NOTE: Result types moved from schema/chart.ts (Issue 5)
export interface IParseChartDataSuccess {
  success: true;
  data: TChartData;
}

export interface IParseChartDataFailure {
  success: false;
  error: string;
}

export type TParseChartDataResult =
  | IParseChartDataSuccess
  | IParseChartDataFailure;
